import type { AdministrativeAreaOption } from "@/components/AdministrativeAreaTreeSelect";

import type { OptionValue, SelectOption, TranslationFn } from "./types";

interface LanguageOption {
  engLabel?: string;
  label: string;
  value: string;
}

export const TRADER_TYPES = {
  institutionalBuyer: "INSTITUTIONAL_BUYER",
  retailer: "RETAILER",
  school: "SCHOOL",
  trader: "TRADER",
  other: "OTHER",
} as const;

const traderTypeOptionConfig = [
  {
    value: TRADER_TYPES.institutionalBuyer,
    labelKey: "traderDetails.form.traderTypes.institutionalBuyer",
  },
  {
    value: TRADER_TYPES.retailer,
    labelKey: "traderDetails.form.traderTypes.retailer",
  },
  {
    value: TRADER_TYPES.school,
    labelKey: "traderDetails.form.traderTypes.school",
  },
  {
    value: TRADER_TYPES.trader,
    labelKey: "traderDetails.form.traderTypes.trader",
  },
  {
    value: TRADER_TYPES.other,
    labelKey: "traderDetails.form.traderTypes.other",
  },
] as const;

/** Maps a raw `trader_type` value to its i18n label key, for read-only display. */
export const TRADER_TYPE_LABEL_KEYS: Record<string, string> = Object.fromEntries(
  traderTypeOptionConfig.map(option => [option.value, option.labelKey])
);

export const createTraderTypeOptions = (t: TranslationFn): SelectOption[] =>
  traderTypeOptionConfig.map(option => ({
    value: option.value,
    label: t(option.labelKey),
  }));

export const adaptTraderTypeForInitialValues = (value: OptionValue | null | undefined) => {
  const option = traderTypeOptionConfig.find(option => option.value === value);

  return option
    ? {
        value: option.value,
        label: option.labelKey,
      }
    : undefined;
};

export const toErrorMessage = (error: unknown) => {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  if (Array.isArray(error)) {
    return error.join(", ");
  }

  if (typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }

  return String(error);
};

export const createLanguageOptions = (languages: LanguageOption[]): SelectOption[] =>
  languages.map(language => ({
    label: language.engLabel ? `${language.engLabel} (${language.label})` : language.label,
    value: language.value,
  }));

export const getLocationLevelPlaceholder = (
  locationValue: AdministrativeAreaOption | null | undefined,
  t: TranslationFn
) => {
  if (!locationValue) {
    return t("aggregatorDetails.form.locationPlaceholder");
  }

  const nextLevel = locationValue.children?.length
    ? [...new Set(locationValue.children.map(child => child.type).filter(Boolean))].join(" / ")
    : "Level";

  return t("aggregatorDetails.form.addLevelOptional", {
    level: nextLevel,
  });
};
